/**
 * BridgeTcpTransport — TcpTransport implementation backed by msgapi.tcp.
 *
 * Works in any msger-family WebView host (msger / msga / msgview / MAUI shell)
 * that exposes msgapi.tcp. TLS handshake happens natively in the host; JS only
 * sees decoded bytes.
 */

import type { TcpTransport } from "./transport.js";

type MsgapiTcpHost = {
    tcp: {
        connect(host: string, port: number, tls: boolean): Promise<number>;
        write(streamId: number, data: string): Promise<void>;
        onData(streamId: number, callback: (data: string) => void): void;
        onClose(streamId: number, callback: (hadError: boolean) => void): void;
        onError(streamId: number, callback: (message: string) => void): void;
        upgradeTLS(streamId: number, servername: string): Promise<void>;
        close(streamId: number): void;
    };
};

/** Resolve the host TCP bridge AT CALL TIME. A bare `msgapi` global was a
 *  ReferenceError whenever the host alias was not injected yet (Android
 *  main-thread mode aliases window.msgapi = _nativeBridge during boot; a
 *  body fetch racing that alias threw "msgapi is not defined" and the UI
 *  mislabeled it "message may have been deleted" — Bob 2026-07-12).
 *  Checking globalThis lazily tolerates any injection order and fails
 *  loud with an actionable message instead of a ReferenceError. */
function bridge(): MsgapiTcpHost {
    const g = globalThis as any;
    const api = g.msgapi || g._nativeBridge;
    if (!api?.tcp) throw new Error("TCP bridge not available — host has not injected msgapi/_nativeBridge (yet)");
    return api;
}

export class BridgeTcpTransport implements TcpTransport {
    private streamId: number | null = null;
    private dataHandler: ((data: Uint8Array) => void) | null = null;
    private closeHandler: ((hadError: boolean) => void) | null = null;
    private errorHandler: ((err: Error) => void) | null = null;
    private _connected = false;
    private encoder = new TextEncoder();

    get connected(): boolean { return this._connected; }

    async connect(host: string, port: number, tls: boolean, _servername?: string): Promise<void> {
        const api = bridge();
        this.streamId = await api.tcp.connect(host, port, tls);
        this._connected = true;

        api.tcp.onData(this.streamId, (data: string) => {
            // The msger bridge currently delivers strings (the WebView IPC
            // contract is JSON-text). Encode once per chunk to honor the
            // byte-stream contract upstream consumers expect. Real fix is to
            // change msgapi.tcp.onData to deliver bytes (via base64 or
            // binary-framed IPC) — that's an msger Rust + JS bridge change,
            // tracked separately. For now the encode is bounded per-chunk,
            // not per-buffer-accumulation, so it doesn't repeat the O(n²)
            // pathology that was in iflow-direct.
            if (this.dataHandler) this.dataHandler(this.encoder.encode(data));
        });
        api.tcp.onClose(this.streamId, (hadError: boolean) => {
            this._connected = false;
            if (this.closeHandler) this.closeHandler(hadError);
        });
        api.tcp.onError(this.streamId, (message: string) => {
            if (this.errorHandler) this.errorHandler(new Error(message));
        });
    }

    async upgradeTLS(servername?: string): Promise<void> {
        if (this.streamId == null) throw new Error("Not connected");
        await bridge().tcp.upgradeTLS(this.streamId, servername || "");
    }

    async write(data: string | Uint8Array): Promise<void> {
        if (this.streamId == null) throw new Error("Not connected");
        const s = typeof data === "string" ? data : new TextDecoder().decode(data);
        await bridge().tcp.write(this.streamId, s);
    }

    onData(handler: (data: Uint8Array) => void): void { this.dataHandler = handler; }
    onClose(handler: (hadError: boolean) => void): void { this.closeHandler = handler; }
    onError(handler: (err: Error) => void): void { this.errorHandler = handler; }

    close(): void {
        if (this.streamId != null) {
            try { bridge().tcp.close(this.streamId); } catch { /* ignore */ }
            this.streamId = null;
            this._connected = false;
        }
    }
}
